// @flow

import { logger } from '../../util/logger'
import isEqual from 'lodash.isequal'
import unionBy from 'lodash.unionby'
import unionWith from 'lodash.unionwith'

import type { DateObject } from '../../util/time'
import type { LocationModel, Location, LocationMETA } from './types'

/**
 * Utility functions for the location reducer
 * @module src/ducks/location/util
 */

/**
 * Convert a date object into a `YYYY-MM-DD`
 * @param  {object} date - Date object
 * @return {string} - Date string
 */
const createDateStringFromDateObject = (date: DateObject): string =>
  `${date.year}-${date.month}-${date.day}`

/**
 * Determine if passed in objects have the same date object
 * @param  {object} obj1 - Object with `date` property
 * @param  {object} obj2 - Object with `date` property
 * @return {boolean} - Whether or not the date objects are the same
 */
const objectsHaveSameDateProperty = (obj1: Object, obj2: Object): boolean => {
  const curDateObj = obj1.date
  const newDateObj = obj2.date
  const curDateString = createDateStringFromDateObject(curDateObj)
  const newDateString = createDateStringFromDateObject(newDateObj)
  return curDateString == newDateString
}

/**
 * Merge META properties
 * @param {object} currentMeta - Current META object
 * @param {object} newMeta - New META object
 * @returns {object} - Merged META object
 */
const mergeMeta = (
  currentMeta: $Shape<LocationMETA> = {},
  newMeta: $Shape<LocationMETA> = {}
): LocationMETA => {
  const meta = {}
  // Merge state objects
  meta.state =
    newMeta.state || currentMeta.state
      ? { ...currentMeta.state, ...newMeta.state }
      : null
  // Merge owner objects
  meta.owner =
    newMeta.owner || currentMeta.owner
      ? { ...currentMeta.owner, ...newMeta.owner }
      : null
  // Merge shared location grants by id `OR` share code
  meta.sharedLocationGrants =
    newMeta.sharedLocationGrants || currentMeta.sharedLocationGrants
      ? unionWith(
          newMeta.sharedLocationGrants,
          currentMeta.sharedLocationGrants,
          (slg1, slg2) => (slg1.id || slg1.code) === (slg2.id || slg2.code)
        )
      : null
  // Merge forecast array by creating ids from their dates
  meta.weatherForecast =
    newMeta.weatherForecast || currentMeta.weatherForecast
      ? unionWith(
          newMeta.weatherForecast,
          currentMeta.weatherForecast,
          objectsHaveSameDateProperty
        )
      : null
  // Merge watering days; NOTE: these are actually scheduled runs, NOT days
  meta.wateringDays =
    newMeta.wateringDays || currentMeta.wateringDays
      ? unionWith(newMeta.wateringDays, currentMeta.wateringDays, isEqual)
      : null
  // Merge weather stations
  meta.weatherStations =
    newMeta.weatherStations || currentMeta.weatherStations
      ? unionBy(
          newMeta.weatherStations,
          currentMeta.weatherStations,
          'stationId'
        )
      : null

  meta.runs = newMeta.runs || currentMeta.runs || null
  meta.skips = newMeta.skips || currentMeta.skips || null

  return { ...currentMeta, ...meta }
}

type LocationData = {
  location: Location & $Shape<LocationModel>,
  state?: Object,
  owner?: Object,
  wateringDays?: Array<Object>,
  weatherForecast?: Array<Object>,
  weatherStations?: Array<Object>,
  skips?: Array<Object>,
  runs?: Array<Object>,
  sharedLocationGrants?: Array<Object>
}
type UpdateFunc = (LocationModel, LocationData) => Object
/**
 * Given a flat data object, create a location for the redux store
 * @param  {Object} currentModel - Current location state
 * @param  {Object} data - Location Summary
 * @return {Object} - Location for store
 */
export const applyLocationModel: UpdateFunc = (
  currentModel = {},
  { location, ...explicitLocationMetaData } = {}
) => {
  const { META } = location || {}
  return {
    ...currentModel,
    ...location,
    // make sure none of the devices are lost; with certain calls, the location's `devices` array may only contain it's
    ...(location && location.devices
      ? { devices: unionBy(location.devices, currentModel.devices, 'id') }
      : {}),
    META: mergeMeta(currentModel.META, {
      ...META,
      ...explicitLocationMetaData
    })
  }
}

/**
 * @summary Update location(s) in store with location(s) from action
 * @description Update data in store with data from action, with optional
 *              transformations; works with a single item or an array
 * @param  {Object} state - Redux state
 * @param  {Object} action - Update action
 * @param  {[Function]} updateFunc - Custom merge function
 * @param  {[Function]} modelTransform - Optional function to transform items
 * @return {Object} - New state
 */
export const update = (
  state: Object = {},
  action: Object = {},
  updateFunc?: UpdateFunc = applyLocationModel,
  modelTransform?: LocationData => LocationData
) => {
  if (Array.isArray(action.payload)) {
    return {
      ...state,
      ...action.payload.reduce((newModels, model) => {
        // Check that we at least got a location
        if (!model || !model.location || !model.location.id) {
          logger.error(
            'CALLED_LOCATION_UPDATE_WITHOUT_CORRECT_LOCATION_SUMMARY',
            action
          )
          return state
        }

        const transformedModel = modelTransform ? modelTransform(model) : model
        const { location: { id } } = transformedModel

        return {
          ...newModels,
          [id]: updateFunc(state[id], transformedModel)
        }
      }, {})
    }
  } else {
    // Check that we at least got a location
    if (
      !action.payload ||
      !action.payload.location ||
      !action.payload.location.id
    ) {
      logger.error(
        'CALLED_LOCATION_UPDATE_WITHOUT_CORRECT_LOCATION_SUMMARY',
        action
      )
      return state
    }

    const transformedModel = modelTransform
      ? modelTransform(action.payload)
      : action.payload
    const { location: { id } } = transformedModel

    return {
      ...state,
      [id]: updateFunc(state[id], transformedModel)
    }
  }
}
